// client/src/hooks/issueTriage/useSubmitBugReport.ts
import { useMutation } from '@tanstack/react-query';
import { getFunctions } from 'firebase/functions';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { invokeNamedCallable } from '../../lib/telemetry/namedCallable';
import { useAuth } from '../../contexts/AuthContext';
import { getCapturedContext } from '../../lib/contextCapture';
import type { SubmitBugReportInput, AnnotationData, SelectedElement, IssueType, IssueSeverity } from '../../types/issue-triage';

interface SubmitBugReportParams {
  title: string;
  description: string;
  issueType: IssueType;
  severity?: IssueSeverity;
  screenshotDataUrl?: string;
  attachmentDataUrls?: string[];
  /** Idempotency key: one per opened report, reused on retry (server dedupes). */
  requestId?: string;
  annotationData?: AnnotationData;
  selectedElement?: SelectedElement;
}

interface SubmitBugReportResult {
  success: boolean;
  reportId: string;
  /** How many attached images the server stored (absent on older servers). */
  attachmentCount?: number;
}

/**
 * Hook for submitting bug reports
 *
 * @example
 * ```tsx
 * const { mutate: submitBugReport, isPending } = useSubmitBugReport();
 *
 * submitBugReport({
 *   title: 'Button not working',
 *   description: 'When I click save, nothing happens',
 *   screenshotDataUrl: 'data:image/png;base64,...'
 * });
 * ```
 */
export function useSubmitBugReport() {
  const { firebaseUser, user } = useAuth();

  return useMutation<SubmitBugReportResult, Error, SubmitBugReportParams>({
    mutationFn: async (params) => {
      if (!firebaseUser || !user) {
        throw new Error('Must be authenticated to submit bug reports');
      }

      // Get captured context
      const context = getCapturedContext(
        firebaseUser.uid,
        firebaseUser.email || '',
        user.role || 'Unknown',
        user.enabledModules || []
      );

      const input: SubmitBugReportInput = {
        title: params.title,
        description: params.description,
        issueType: params.issueType,
        severity: params.severity,
        screenshotDataUrl: params.screenshotDataUrl,
        attachmentDataUrls: params.attachmentDataUrls,
        requestId: params.requestId,
        annotationData: params.annotationData,
        selectedElement: params.selectedElement,
        context,
      };

      const functions = getFunctions();
      const submitIssueReport = httpsCallable<SubmitBugReportInput, SubmitBugReportResult>(
        functions,
        'submitIssueReport'
      );

      // Named: a failure here is a user unable to REPORT a bug, and it used to
      // surface as an anonymous FirebaseError (OFFICEHUB-CLIENT-69 shape).
      const result = await invokeNamedCallable('submitIssueReport', () =>
        submitIssueReport(input),
      );
      return result.data;
    },
  });
}
